import { FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form';

type Props<
  T extends FieldValues,
  U extends { id: string },
  ArrayType extends Path<T>,
  SelectorType extends Path<T>,
> = {
  form: UseFormReturn<T>;
  selectorFieldName: SelectorType;
  arrayFieldName: ArrayType;
  allElements: U[];

  onAddInterceptor?: (idToAdd: string, currentArray: string[], newArray: string[]) => string[];
  onRemoveInterceptor?: (
    idToRemove: string,
    currentArray: string[],
    newArray: string[]
  ) => string[];
};

/**
 * This hook is used to add and remove items from a field array.
 * Usage:
 * const { handleAdd, handleRemove, getAvailableItems } = useFormFieldArrayHelpers({
 *   form,
 *   selectorFieldName: 'selectedClientID',
 *   arrayFieldName: 'clientIDs',
 *   allElements: allClients,
 * });
 */
export function useFormFieldArrayHelpers<
  T extends FieldValues,
  U extends { id: string },
  ArrayType extends Path<T>,
  SelectorType extends Path<T>,
>({
  form,
  selectorFieldName,
  arrayFieldName,
  allElements,
  onAddInterceptor,
  onRemoveInterceptor,
}: Props<T, U, ArrayType, SelectorType>) {
  type SelectorFieldValue = PathValue<T, SelectorType>;
  const currentArrayElements: string[] = form.watch(arrayFieldName);

  const handleAdd = () => {
    const selectedId = form.getValues(selectorFieldName);
    if (selectedId && !currentArrayElements.includes(selectedId)) {
      const expectedNewArray = [...currentArrayElements, selectedId];
      const newArray = onAddInterceptor
        ? onAddInterceptor(selectedId, form.getValues(arrayFieldName), expectedNewArray)
        : expectedNewArray;
      form.setValue(arrayFieldName, newArray as PathValue<T, ArrayType>);
      form.setValue(selectorFieldName, undefined as SelectorFieldValue);
    }
  };

  const handleRemove = (id: string) => {
    const expectedNewArray = currentArrayElements.filter((el) => el !== id);
    const newArray = onRemoveInterceptor
      ? onRemoveInterceptor(id, currentArrayElements, expectedNewArray)
      : expectedNewArray;
    form.setValue(arrayFieldName, newArray as PathValue<T, ArrayType>);
  };

  const getAvailableItems = () =>
    allElements.filter((elem) => !currentArrayElements.includes(elem.id)) || [];

  return {
    handleAdd,
    handleRemove,
    getAvailableItems,
  };
}
